import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, Copy, FileSearch, Loader } from 'lucide-react';
import type {
  CacheEnvelope,
  DumpIndexEntry,
  PerformanceDump,
  ThrottleServerDumps,
} from '../../types/throttle';
import { throttleApiService } from '../../services/throttleApi';
import {
  buildTeleportCommand,
  chunkCenterBlock,
  chunkMsptThresholdClass,
  formatDimension,
  formatEpochMsAgo,
  normalizeChunkSample,
} from '../../utils/throttle';
import { cn } from '../../utils/cn';
import { SectionHeader } from '../ui/SectionHeader';

interface PerformanceDumpsPanelProps {
  fqdn: string;
  /** The `dumps` slice of the merged server detail response. */
  dumps: ThrottleServerDumps;
  /**
   * Lowest MSPT at which this server's effective rules flag a regular chunk
   * (see `getFlaggingFloorMspt`); `null` = built-in defaults / unknown.
   */
  flaggingThreshold: number | null;
}

/** Dump tables can hold thousands of rows — reveal them in pages client-side. */
const ROWS_PER_PAGE = 100;

const describeAnalytics = (dump: PerformanceDump): string | null => {
  const analytics = dump.analytics;
  if (!analytics) return null;
  const parts: string[] = [];
  if (analytics.tps != null) parts.push(`${analytics.tps.toFixed(1)} TPS`);
  if (analytics.mspt != null) parts.push(`${analytics.mspt.toFixed(1)} ms/t`);
  const context: string[] = [];
  if (analytics.playerCount != null) context.push(`${analytics.playerCount} player(s)`);
  if (analytics.entities != null) context.push(`${analytics.entities.toLocaleString()} entities`);
  if (analytics.chunks != null) context.push(`${analytics.chunks.toLocaleString()} loaded chunks`);
  if (parts.length === 0 && context.length === 0) return null;
  const load = parts.join(' / ');
  const detail = context.join(', ');
  if (load && detail) return `Taken while the server ran at ${load} with ${detail}.`;
  if (load) return `Taken while the server ran at ${load}.`;
  return `Taken with ${detail}.`;
};

/**
 * Staff-triggered performance dumps for one server: metadata list + full
 * chunk-table viewer. Dumps appear when staff run `/q chunkPerformance
 * dumpData` in-game and expire 24 h after they were taken — an empty list is
 * the normal state, and a 404 on a full dump means it just expired.
 */
export const PerformanceDumpsPanel = ({ fqdn, dumps, flaggingThreshold }: PerformanceDumpsPanelProps) => {
  const [entries, setEntries] = useState<DumpIndexEntry[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [selectedN, setSelectedN] = useState<number | null>(null);
  const [selectedDump, setSelectedDump] = useState<CacheEnvelope<PerformanceDump> | null>(null);
  const [isDumpLoading, setIsDumpLoading] = useState(false);
  const [dumpError, setDumpError] = useState<string | null>(null);
  const [dimensionFilter, setDimensionFilter] = useState<string>('all');
  const [visibleRowCount, setVisibleRowCount] = useState(ROWS_PER_PAGE);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const refreshList = useCallback(async () => {
    try {
      const result = await throttleApiService.getServerDumps(fqdn);
      setEntries(result?.entries ?? []);
      setListError(null);
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to load dump list');
    }
  }, [fqdn]);

  // Only servers with live dumps need the metadata list.
  useEffect(() => {
    setEntries(null);
    setSelectedN(null);
    setSelectedDump(null);
    setDumpError(null);
    if (dumps.count > 0) {
      void refreshList();
    } else {
      setEntries([]);
    }
    // dumps.lastDumpAt re-triggers the fetch when a new dump appears mid-session.
  }, [refreshList, dumps.count, dumps.lastDumpAt]);

  const openDump = useCallback(
    async (entry: DumpIndexEntry) => {
      setSelectedN(entry.n);
      setDimensionFilter('all');
      setVisibleRowCount(ROWS_PER_PAGE);
      setDumpError(null);

      // The merged endpoint already embeds the newest full dump — reuse it when
      // the clicked entry is provably that dump (the list can be a poll ahead).
      const newestN = entries != null && entries.length > 0
        ? Math.max(...entries.map((e) => e.n))
        : null;
      if (
        dumps.latest &&
        entry.n === newestN &&
        dumps.lastDumpAt != null &&
        entry.dumpedAt === dumps.lastDumpAt
      ) {
        setSelectedDump(dumps.latest);
        return;
      }

      setIsDumpLoading(true);
      setSelectedDump(null);
      try {
        const dump = await throttleApiService.getDump(fqdn, entry.n);
        if (dump === null) {
          // Expired since the metadata list was cached — refresh the list.
          setDumpError('This dump has expired (dumps live for 24 h). The list has been refreshed.');
          void refreshList();
        } else {
          setSelectedDump(dump);
        }
      } catch (err) {
        setDumpError(err instanceof Error ? err.message : 'Failed to load dump');
      } finally {
        setIsDumpLoading(false);
      }
    },
    [dumps.latest, entries, fqdn, refreshList],
  );

  const chunks = useMemo(
    () => (selectedDump ? selectedDump.data.chunks.map(normalizeChunkSample) : []),
    [selectedDump],
  );

  const dimensions = useMemo(
    () => Array.from(new Set(chunks.map((chunk) => chunk.dimension))),
    [chunks],
  );

  const filteredChunks = useMemo(
    () =>
      dimensionFilter === 'all'
        ? chunks
        : chunks.filter((chunk) => chunk.dimension === dimensionFilter),
    [chunks, dimensionFilter],
  );

  const visibleChunks = filteredChunks.slice(0, visibleRowCount);

  const handleCopyTeleport = (key: string, dimension: string, posX: number, posZ: number) => {
    navigator.clipboard.writeText(buildTeleportCommand(dimension, posX, posZ)).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((prev) => (prev === key ? null : prev)), 2000);
    });
  };

  const analyticsSummary = selectedDump ? describeAnalytics(selectedDump.data) : null;

  // Same threshold comparison as the flagged-chunks table: dump rows at or
  // above the flagging threshold stand out, everything else stays neutral.
  const threshold = flaggingThreshold;

  return (
    <div className="glass-card p-4 md:p-5">
      <SectionHeader
        as="h2"
        className="mb-1"
        icon={FileSearch}
        iconClassName="text-blue-300"
        title="Performance Dumps"
        count={dumps.count > 0 ? dumps.count : undefined}
      />
      <p className="text-xs text-glass-500 mb-4">
        Staff-triggered deep dives (<span className="font-mono">/q chunkPerformance dumpData</span>)
        containing every sampled chunk, slowest first. Dumps expire 24 h after they were taken —
        an empty list just means nobody needed to investigate recently.
      </p>

      {listError && (
        <div className="alert alert-warning mb-4">
          <AlertTriangle />
          <p className="text-sm">{listError}</p>
        </div>
      )}

      {dumps.count === 0 ? (
        <p className="text-sm text-glass-400">No live performance dumps for this server.</p>
      ) : entries === null ? (
        <div className="flex items-center gap-2 text-sm text-glass-400">
          <Loader className="w-4 h-4 animate-spin" />
          <span>Loading dump list...</span>
        </div>
      ) : entries.length === 0 ? (
        <p className="text-sm text-glass-400">No live performance dumps for this server.</p>
      ) : (
        <>
          {/* Dump metadata list */}
          <div className="subcard overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th className="th px-3">Taken</th>
                  <th className="th px-3 text-right">Chunks sampled</th>
                  <th className="th px-3 text-right" title="Highest MSPT among the sampled chunks">
                    Worst chunk
                  </th>
                  <th className="th px-3" title="Server-wide load at dump time">
                    Server at dump time
                  </th>
                  <th className="th px-3 text-right" />
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr
                    key={entry.n}
                    className={cn(
                      'cursor-pointer',
                      selectedN === entry.n && 'row-active',
                    )}
                    onClick={() => void openDump(entry)}
                  >
                    <td className="td px-3 py-2 text-white font-medium tabular-nums">
                      {entry.dumpedAt != null ? (
                        <span title={new Date(entry.dumpedAt).toLocaleString()}>
                          {formatEpochMsAgo(entry.dumpedAt)}
                        </span>
                      ) : (
                        <span title={`Cached ${new Date(entry.cachedAt).toLocaleString()}`}>
                          cached {formatEpochMsAgo(entry.cachedAt)}
                        </span>
                      )}
                    </td>
                    <td className="td px-3 py-2 text-glass-300 tabular-nums text-right">
                      {entry.chunkCount.toLocaleString()}
                    </td>
                    <td className="td px-3 py-2 text-glass-300 tabular-nums text-right">
                      {entry.worstChunkMspt != null ? `${entry.worstChunkMspt.toFixed(1)} ms` : '—'}
                    </td>
                    <td className="td px-3 py-2 text-glass-300 tabular-nums">
                      {entry.analytics?.tps != null || entry.analytics?.mspt != null
                        ? [
                            entry.analytics?.tps != null ? `${entry.analytics.tps.toFixed(1)} TPS` : null,
                            entry.analytics?.mspt != null ? `${entry.analytics.mspt.toFixed(1)} ms/t` : null,
                          ]
                            .filter(Boolean)
                            .join(' / ')
                        : '—'}
                    </td>
                    <td className="td px-3 py-2 text-right">
                      <span className="text-xs font-medium text-accent-300">
                        {selectedN === entry.n ? 'Viewing' : 'View'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {dumpError && (
            <div className="alert alert-warning mt-4">
              <AlertTriangle />
              <p className="text-sm">{dumpError}</p>
            </div>
          )}

          {isDumpLoading && (
            <div className="flex items-center gap-2 text-sm text-glass-400 mt-4">
              <Loader className="w-4 h-4 animate-spin" />
              <span>Loading chunk table...</span>
            </div>
          )}

          {/* Full chunk table for the selected dump */}
          {selectedDump && !isDumpLoading && (
            <div className="mt-4 border-t border-glass-200/10 pt-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                <div>
                  {analyticsSummary && <p className="text-sm text-glass-300">{analyticsSummary}</p>}
                  <p className="text-xs text-glass-500 tabular-nums">
                    {filteredChunks.length.toLocaleString()} chunk
                    {filteredChunks.length === 1 ? '' : 's'}
                    {dimensionFilter !== 'all' && ` in ${formatDimension(dimensionFilter)}`}, slowest
                    first · cached {formatEpochMsAgo(selectedDump.fetchedAt)}
                  </p>
                </div>
                {dimensions.length > 1 && (
                  <select
                    className="glass-select"
                    value={dimensionFilter}
                    onChange={(e) => {
                      setDimensionFilter(e.target.value);
                      setVisibleRowCount(ROWS_PER_PAGE);
                    }}
                    title="Filter by dimension"
                  >
                    <option value="all">All dimensions</option>
                    {dimensions.map((dimension) => (
                      <option key={dimension} value={dimension}>
                        {formatDimension(dimension)}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="subcard overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="th px-3">Dimension</th>
                      <th className="th px-3" title="Chunk coordinates (multiply by 16 for blocks)">
                        Chunk
                      </th>
                      <th className="th px-3" title="Block coordinates of the chunk center">
                        Block center
                      </th>
                      <th className="th px-3" title="Average ms/tick this chunk cost in the sampling window">
                        Chunk MSPT
                      </th>
                      <th className="th px-3 text-right">Teleport</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleChunks.map((chunk, index) => {
                      const key = `${chunk.dimension}:${chunk.posX}:${chunk.posZ}:${index}`;
                      const center = chunkCenterBlock(chunk.posX, chunk.posZ);
                      return (
                        <tr key={key}>
                          <td className="td px-3 py-2 text-glass-300" title={chunk.dimension}>
                            {formatDimension(chunk.dimension)}
                          </td>
                          <td className="td px-3 py-2 text-white font-medium font-mono text-xs tabular-nums">
                            {chunk.posX}, {chunk.posZ}
                          </td>
                          <td className="td px-3 py-2 text-glass-300 font-mono text-xs tabular-nums">
                            {center.x}, {center.z}
                          </td>
                          <td className="td px-3 py-2">
                            <span
                              className={cn(
                                'font-medium tabular-nums',
                                threshold != null
                                  ? chunkMsptThresholdClass(chunk.mspt, threshold)
                                  : 'text-glass-300',
                              )}
                            >
                              {chunk.mspt.toFixed(2)} ms
                            </span>
                            {threshold != null && (
                              <span className="ml-2 text-xs text-glass-500 tabular-nums">
                                {(chunk.mspt / threshold).toFixed(1)}× threshold
                              </span>
                            )}
                          </td>
                          <td className="td px-3 py-2 text-right">
                            <button
                              onClick={() =>
                                handleCopyTeleport(key, chunk.dimension, chunk.posX, chunk.posZ)
                              }
                              className="glass-button-sm"
                              title={buildTeleportCommand(chunk.dimension, chunk.posX, chunk.posZ)}
                            >
                              {copiedKey === key ? (
                                <Check className="w-3.5 h-3.5 text-green-400" />
                              ) : (
                                <Copy className="w-3.5 h-3.5" />
                              )}
                              {copiedKey === key ? 'Copied' : 'Copy /tp'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {filteredChunks.length > visibleRowCount && (
                <button
                  onClick={() => setVisibleRowCount((count) => count + ROWS_PER_PAGE)}
                  className="glass-button-sm mt-3"
                >
                  Show {Math.min(ROWS_PER_PAGE, filteredChunks.length - visibleRowCount)} more (
                  {(filteredChunks.length - visibleRowCount).toLocaleString()} remaining)
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
